import React, { useState, useEffect, useRef } from 'react'; 
import { useNavigate } from 'react-router-dom';

import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  MoreVertical, 
  UserPlus, 
  Phone, 
  GraduationCap, 
  Bus as BusIcon, 
  Filter,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Users,
  Printer,
  Upload,
  Download,
  FileSpreadsheet,
  ChevronRight,
  ChevronLeft
} from 'lucide-react';
import { exportToExcel } from '../../utils/excelExport';
import { Card, Button, Input, Select, Badge } from '../../components/ui/index.jsx';

import { Modal } from '../../components/ui/Modal';
import { useGet, useMutation } from '../../hooks/useApi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const StudentList = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('');
    const [selectedRegion, setSelectedRegion] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedInstitution, setSelectedInstitution] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [isPrintFilterModalOpen, setIsPrintFilterModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [includeInactive, setIncludeInactive] = useState(false);
    const { hasPermission } = useAuth();
    
    const fileInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const { success, error: toastError } = useToast();

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setIsUploading(true);
        try {
            const res = await api.post('/students/import', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            success(res.data.message || 'تم رفع البيانات بنجاح');
            refreshStudents();
        } catch (error) {
            toastError(error.response?.data?.message || 'حدث خطأ أثناء رفع بيانات التلاميذ. يرجى التحقق من الملف.');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };
    
    // Check Permissions
    const canCreate = hasPermission('students', 'create');
    const canUpdate = hasPermission('students', 'update');
    const canDelete = hasPermission('students', 'delete');
    
    // Data Fetching - Using server-side search and pagination
    const { data: studentsResponse, loading: studentsLoading, refresh: refreshStudents } = useGet(
        `/students?page=${currentPage}&search=${searchTerm}&school_class_id=${selectedClass}&group_id=${selectedGroup}&academic_year_id=${selectedYear}&region_id=${selectedRegion}&institution_id=${selectedInstitution}&status=${selectedStatus}&include_inactive=${includeInactive}`
    );
    const { data: regionsRaw } = useGet('/regions?all=true');
    const { data: busesRaw } = useGet('/buses?all=true');
    const { data: academicYearsRaw } = useGet('/academic-years');
    const { data: schoolClassesRaw } = useGet('/school-classes?all=true');
    const { data: groupsRaw } = useGet('/groups?all=true');
    const { data: institutionsRaw } = useGet('/institutions');

    // Safely extract arrays (API may return paginated { data: [], meta: {} } or plain [])
    const regions = Array.isArray(regionsRaw) ? regionsRaw : (regionsRaw?.data || []);
    const buses = Array.isArray(busesRaw) ? busesRaw : (busesRaw?.data || []);
    const academicYears = Array.isArray(academicYearsRaw) ? academicYearsRaw : (academicYearsRaw?.data || []);
    const schoolClasses = Array.isArray(schoolClassesRaw) ? schoolClassesRaw : (schoolClassesRaw?.data || []);
    const groups = Array.isArray(groupsRaw) ? groupsRaw : (groupsRaw?.data || []);
    const institutions = Array.isArray(institutionsRaw) ? institutionsRaw : (institutionsRaw?.data || []);

    // Students are now in data.data if paginated, or just data if not
    const students = studentsResponse?.data || studentsResponse || [];
    const pagination = studentsResponse?.meta || null;   

    const handleExportExcel = async () => {
        try {
            const response = await api.get(`/students?all=true&search=${searchTerm}&school_class_id=${selectedClass}&group_id=${selectedGroup}&academic_year_id=${selectedYear}&region_id=${selectedRegion}&institution_id=${selectedInstitution}&status=${selectedStatus}&include_inactive=${includeInactive}`);
            const studentsToExport = response.data?.data || response.data || [];
            
            const data = studentsToExport.map(s => ({
                'الاسم الكامل': s.full_name,
                'رقم مسار': s.code_massar,
                'رمز QR': s.qr_code,
                'المؤسسة': s.institution?.name || '--',
                'المستوى': s.school_class?.name || '--',
                'المجموعة': s.group?.name || '--',
                'المنطقة': s.region?.region_name || '--',
                'المكان': s.place?.place_name || '--',
                'الجنس': s.gender === 'Male' ? 'ذكر' : 'أنثى',
                'الهاتف': s.phone,
                'الحالة': s.status || 'عادية',
                'نشط': s.is_active ? 'نعم' : 'لا'
            }));
            
            exportToExcel(data, `قائمة_الطلاب_${new Date().toISOString().split('T')[0]}`);
        } catch (error) {
            console.error("Export failed:", error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('هل أنت متأكد من حذف هذا الطالب؟')) {
            try {
                await api.delete(`/students/${id}`);
                success('تم حذف الطالب بنجاح');
                refreshStudents();
            } catch (err) {
                toastError('خطأ أثناء الحفظ');
            }
        }
    };

    const handleEdit = (student) => {
        setSelectedStudent(student);
        setIsModalOpen(true);
    };

    const handleAdd = () => {
        setSelectedStudent(null);
        setIsModalOpen(true);
    };

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl  text-slate-800">قائمة الطلاب</h2>
                    <p className="text-slate-500 text-[11px] leading-none mt-1">إدارة بيانات الطلاب المسجلين في خدمة النقل</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                    <Button variant="secondary" onClick={() => setIsPrintModalOpen(true)} className="w-full sm:w-auto bg-white hover:bg-slate-50 text-slate-700 border border-slate-200">
                        <Printer className="w-4 h-4 ml-2" />
                        طباعة البطاقات
                    </Button>
                    <Button 
                        variant="outline" 
                        onClick={() => setIsPrintFilterModalOpen(true)} 
                        className="w-full sm:w-auto bg-white text-slate-700"
                    >
                        <Printer className="w-4 h-4 ml-2" />
                        لائحة الطلاب
                    </Button>
                    <input type="file" accept=".csv" ref={fileInputRef} className="hidden" onChange={handleFileUpload} />
                    {canCreate && (
                        <div className="flex gap-2 w-full sm:w-auto">
                            
                            <div className="flex gap-2">
                            <Button 
                                variant="outline" 
                                onClick={() => setIsTransferModalOpen(true)}
                                className="border-slate-200 text-slate-600"
                            >
                                <Users className="w-4 h-4 ml-2 text-blue-600" />
                                نقل التلاميذ
                            </Button>
                            <Button 
                                variant="outline" 
                                onClick={handleExportExcel}
                                className="border-slate-200 text-slate-600"
                            >
                                <FileSpreadsheet className="w-4 h-4 ml-2 text-green-600" />
                                تحميل Excel
                            </Button>
                            <Button className="bg-primary text-white" onClick={handleAdd}>
                                <Plus className="w-4 h-4 ml-2" />
                                إضافة طالب جديد
                            </Button>
                        </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Filters Card */}
            <Card className="p-4 bg-white/50 backdrop-blur-sm border-slate-100">
                <div className="flex flex-col gap-4">
                    {/* Search - Full Width */}
                    <div className="w-full">
                        <Input 
                            placeholder="بحث بالاسم، رقم مسار أو QR..." 
                            icon={Search}
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1); // Reset to first page on search
                            }}
                            className="w-full"
                        />
                    </div>
                    
                    {/* Select Filters - Horizontal Scrollable Row */}
                    <div className="flex flex-row overflow-x-auto gap-3 pb-2 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                        <Select 
                            className="bg-white min-w-[160px] flex-1"
                            options={[
                                { value: '', label: 'كل المؤسسات' },
                                ...(institutions?.map(i => ({ value: i.id, label: i.name })) || [])
                            ]}
                            value={selectedInstitution}
                            onChange={(e) => { setSelectedInstitution(e.target.value); setCurrentPage(1); }}
                        />
                        <Select 
                            className="bg-white min-w-[140px] flex-1"
                            options={[
                                { value: '', label: 'كل السنوات' },
                                ...(academicYears?.map(ay => ({ value: ay.id, label: ay.name })) || [])
                            ]}
                            value={selectedYear}
                            onChange={(e) => { setSelectedYear(e.target.value); setCurrentPage(1); }}
                        />
                        <Select 
                            className="bg-white min-w-[140px] flex-1"
                            options={[
                                { value: '', label: 'كل الأقسام' },
                                ...(schoolClasses?.map(sc => ({ value: sc.id, label: sc.name })) || [])
                            ]}
                            value={selectedClass}
                            onChange={(e) => { setSelectedClass(e.target.value); setCurrentPage(1); }}
                        />
                        <Select 
                            className="bg-white min-w-[140px] flex-1"
                            options={[
                                { value: '', label: 'كل المجموعات' },
                                ...(groups?.map(g => ({ value: g.id, label: g.name })) || [])
                            ]}
                            value={selectedGroup}
                            onChange={(e) => { setSelectedGroup(e.target.value); setCurrentPage(1); }}
                        />
                        <Select 
                            className="bg-white min-w-[140px] flex-1"
                            options={[
                                { value: '', label: 'كل المناطق' },
                                ...(regions?.map(r => ({ value: r.id, label: r.region_name })) || [])
                            ]}
                            value={selectedRegion}
                            onChange={(e) => { setSelectedRegion(e.target.value); setCurrentPage(1); }}
                        />
                        <Select 
                            className="bg-white min-w-[160px] flex-1"
                            options={[
                                { value: '', label: 'كل الأوضاع' },
                                { value: 'حالة طبيعية', label: 'حالة طبيعية' },
                                { value: 'يتيم (ة)', label: 'يتيم (ة)' },
                                { value: 'ذوي الاحتياجات خاصة', label: 'ذوي الاحتياجات خاصة' },
                                { value: 'عائلة معوزة', label: 'عائلة معوزة' },
                                { value: 'مرض مزمن (اب)', label: 'مرض مزمن (اب)' },
                                { value: 'مرض مزمن (أم)', label: 'مرض مزمن (أم)' }
                            ]}
                            value={selectedStatus}
                            onChange={(e) => { setSelectedStatus(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                    
                    {/* Additional Options */}
                    <div className="flex items-center gap-3 pt-2 border-t border-slate-50">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-slate-200">
                            <input 
                                type="checkbox" 
                                id="includeInactive"
                                className="w-4 h-4 text-primary focus:ring-primary/20 border-slate-300 rounded"
                                checked={includeInactive}
                                onChange={(e) => {
                                    setIncludeInactive(e.target.checked);
                                    setCurrentPage(1);
                                }}
                            />
                            <label htmlFor="includeInactive" className="text-[11px] text-slate-600 cursor-pointer select-none">إظهار المغادرين</label>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Table Card */}
            <Card className="border-slate-100">
                <div className="overflow-x-auto">
                    <table className="w-full text-right">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-100">
                                <th className="px-6 py-2.5 text-[10px]  text-slate-500 uppercase tracking-wider">الطالب</th>
                                <th className="px-6 py-2.5 text-[10px]  text-slate-500 uppercase tracking-wider">المؤسسة والقسم</th>
                                <th className="px-6 py-2.5 text-[10px]  text-slate-500 uppercase tracking-wider">الحافلة والمنطقة / المكان</th>
                                <th className="px-6 py-2.5 text-[10px]  text-slate-500 uppercase tracking-wider">الحالة</th>
                                <th className="px-6 py-2.5 text-[10px]  text-slate-500 uppercase tracking-wider">الإجراءات</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {studentsLoading ? (
                                Array(5).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="5" className="px-6 py-8">
                                            <div className="h-4 bg-slate-100 rounded w-full"></div>
                                        </td>
                                    </tr>
                                ))
                            ) : students.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-16 text-center">
                                        <div className="max-w-xs mx-auto">
                                            <div className="w-16 h-16 bg-slate-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                                                <Users className="w-8 h-8 text-slate-300" />
                                            </div>
                                            <h4 className="text-slate-800  mb-1">لا يوجد طلاب</h4>
                                            <p className="text-slate-400 text-sm">لم يتم العثور على أي طالب يطابق معايير البحث.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                students.map((student) => (
                                    <tr key={student.id} className="hover:bg-slate-50/80 transition-colors group">
                                        <td className="px-6 py-3.5">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-orange-50 text-primary flex items-center justify-center  text-sm shrink-0">

                                                    {student.full_name.charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="text-sm  text-slate-800">{student.full_name}</p>
                                                    <p className="text-[10px]  text-slate-400 uppercase tracking-tight">{student.code_massar}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-3.5">
                                            <div className="flex flex-col">
                                                <span className="text-sm text-slate-600 font-medium inline-flex items-center gap-1.5">
                                                    <GraduationCap className="w-3.5 h-3.5 text-slate-400" />
                                                    {student.institution?.name || '--'}
                                                </span>
                                                <span className="text-[11px] text-slate-400  mr-5">{student.school_class?.name || '--'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-3.5">
                                            <div className="flex flex-col">
                                                <span className="text-sm text-slate-600 font-medium inline-flex items-center gap-1.5">
                                                    <Filter className="w-3.5 h-3.5 text-slate-400" />
                                                    {student.region?.region_name || 'غير محدد'}
                                                </span>
                                                <span className="text-[11px] text-slate-400 mr-5">
                                                    {student.place?.place_name || ''}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-3.5">
                                            {student.is_active == 1 ? (
                                                <Badge variant="success">نشط</Badge>
                                            ) : (
                                                <Badge variant="danger">غادر</Badge>
                                            )}
                                        </td>
                                        <td className="px-6 py-3.5">
                                            <div className="flex items-center gap-1">
                                                {canUpdate && (
                                                    <button 
                                                        onClick={() => handleEdit(student)}
                                                        className="p-2 rounded-lg text-slate-400 hover:text-primary hover:bg-orange-50 transition-all"
                                                    >
                                                        <Edit className="w-4.5 h-4.5" />
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button 
                                                        onClick={() => handleDelete(student.id)}
                                                        className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all"
                                                    >
                                                        <Trash2 className="w-4.5 h-4.5" />
                                                    </button>
                                                )}
                                                {!canUpdate && !canDelete && (
                                                    <span className="text-[10px] text-slate-300">لا توجد صلاحيات</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {pagination && pagination.last_page > 1 && (
                    <div className="px-6 py-4 bg-slate-50/30 border-t border-slate-100 flex items-center justify-between">
                        <div className="text-xs text-slate-500 font-normal">
                            عرض <span className="font-medium text-slate-700">{pagination.from}</span> إلى <span className="font-medium text-slate-700">{pagination.to}</span> من أصل <span className="font-medium text-slate-700">{pagination.total}</span> طالب
                        </div>
                        <div className="flex items-center gap-2">
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="h-8 px-3 text-xs"
                            >
                                السابق
                            </Button>
                            <div className="flex items-center gap-1">
                                {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                    // Basic pagination logic to show around current page
                                    let pageNum = i + 1;
                                    if (pagination.last_page > 5 && currentPage > 3) {
                                        pageNum = currentPage - 3 + i + 1;
                                        if (pageNum > pagination.last_page) pageNum = pagination.last_page - (4 - i);
                                    }
                                    
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`w-8 h-8 rounded-lg text-xs transition-all ${
                                                currentPage === pageNum 
                                                ? 'bg-primary text-white shadow-sm font-medium' 
                                                : 'text-slate-500 hover:bg-slate-100'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                                disabled={currentPage === pagination.last_page}
                                className="h-8 px-3 text-xs"
                            >
                                التالي
                            </Button>
                        </div>
                    </div>
                )}
            </Card>

            {/* Modal for Add/Edit */}
            <Modal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                title={selectedStudent ? 'تعديل بيانات الطالب' : 'إضافة طالب جديد'}
            >
                <StudentForm 
                    key={selectedStudent?.id || 'new'}
                    student={selectedStudent} 
                    onSuccess={() => {
                        setIsModalOpen(false);
                        refreshStudents();
                    }}
                    regions={regions}
                    buses={buses}
                    academicYears={academicYears}
                    schoolClasses={schoolClasses}
                    groups={groups}
                    institutions={institutions}
                />
            </Modal>

            <Modal 
                isOpen={isPrintModalOpen} 
                onClose={() => setIsPrintModalOpen(false)} 
                title="تحديد التلاميذ للطباعة"
            >
                <PrintSelectionModal 
                    onClose={() => setIsPrintModalOpen(false)} 
                />
            </Modal>

            <Modal 
                isOpen={isTransferModalOpen} 
                onClose={() => setIsTransferModalOpen(false)} 
                title="نقل التلاميذ دفعة واحدة"
            >
                <TransferModal
                    onClose={() => setIsTransferModalOpen(false)} 
                    schoolClasses={schoolClasses}
                    onSuccess={refreshStudents}
                />
            </Modal>

            {/* Modal for Print Filters */}
            <Modal 
                isOpen={isPrintFilterModalOpen} 
                onClose={() => setIsPrintFilterModalOpen(false)} 
                title="خيارات طباعة لائحة التلاميذ"
            >
                <StudentPrintModal 
                    onClose={() => setIsPrintFilterModalOpen(false)} 
                    regions={regions}
                    schoolClasses={schoolClasses}
                    groups={groups}
                    academicYears={academicYears}
                    institutions={institutions}
                />
            </Modal>
        </div>
    );
};

const StudentForm = ({ student, onSuccess, regions, buses, academicYears, schoolClasses, groups, institutions }) => {
    const [formData, setFormData] = useState({
        full_name: student?.full_name || '',
        code_massar: student?.code_massar || '',
        qr_code: student?.qr_code || '',
        parent_name: student?.parent_name || '',
        phone: student?.phone || '',
        address: student?.address || '',
        institution_id: student?.institution_id ? String(student.institution_id) : '',
        school_class_id: student?.school_class_id ? String(student.school_class_id) : '',
        region_id: student?.region_id ? String(student.region_id) : (student?.region?.id ? String(student.region.id) : ''),
        place_id: student?.place_id ? String(student.place_id) : (student?.place?.id ? String(student.place.id) : ''),
        academic_year_id: student?.academic_year_id ? String(student.academic_year_id) : '',
        group_id: student?.group_id ? String(student.group_id) : '',
        level: student?.level || '',
        gender: student?.gender || '',
        subscription_price: student?.subscription_price || '',
        registration_fee: student?.registration_fee || '',
        exit_date: student?.exit_date || '',
        exit_reason: student?.exit_reason || '',
        status: student?.status || 'حالة طبيعية'
    });

    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [places, setPlaces] = useState([]);
    const [placesLoading, setPlacesLoading] = useState(false);
    const [isFetchingQr, setIsFetchingQr] = useState(false);

    // Fetch next QR code for new students
    useEffect(() => {
        if (!student) {
            setIsFetchingQr(true);
            api.get('/students/next-qr')
                .then(res => {
                    if (res.data?.next_qr) {
                        setFormData(prev => ({ ...prev, qr_code: res.data.next_qr }));
                    }
                })
                .catch(err => console.error("Error fetching next QR code:", err))
                .finally(() => setIsFetchingQr(false));
        }
    }, [student]);

    // Fetch places when region changes or when editing (pre-load region places)
    useEffect(() => {
        if (formData.region_id) {
            setPlacesLoading(true);
            api.get(`/regions/${formData.region_id}/places`)
                .then(res => {
                    setPlaces(res.data || []);
                })
                .catch(() => setPlaces([]))
                .finally(() => setPlacesLoading(false));
        } else {
            setPlaces([]);
        }
    }, [formData.region_id]);
    
    // Safety sync: Ensure place_id matches the student data once places are loaded
    useEffect(() => {
        if (student && places.length > 0) {
            const studentPlaceId = student.place_id ? String(student.place_id) : (student.place?.id ? String(student.place.id) : null);
            // Only sync if formData.place_id is empty and we have a value from student
            if (studentPlaceId && !formData.place_id && formData.region_id === String(student.region_id || student.region?.id)) {
                setFormData(prev => ({ ...prev, place_id: studentPlaceId }));
            }
        }
    }, [places, student, formData.region_id]);

    const handleRegionChange = (e) => {
        setFormData({ ...formData, region_id: e.target.value, place_id: '' });
    };

    const { success, error: toastError } = useToast();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrors({});

        try {
            if (student) {
                await api.put(`/students/${student.id}`, formData);
                success('تم تحديث بيانات الطالب بنجاح');
            } else {
                await api.post('/students', formData);
                success('تم إضافة الطالب بنجاح');
            }
            onSuccess();
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data);
            } else {
                toastError('حدث خطأ أثناء الحفظ');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input 
                    label="الاسم الكامل" 
                    placeholder="أدخل الاسم الرباعي" 
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    error={errors.full_name?.[0]}
                />
                <Input 
                    label="رقم مسار" 
                    placeholder="مثال: J132456789" 
                    value={formData.code_massar}
                    onChange={(e) => setFormData({...formData, code_massar: e.target.value})}
                    error={errors.code_massar?.[0]}
                />
                <Input 
                    label="رمز QR" 
                    placeholder={isFetchingQr ? "جاري توليد الرمز..." : "رمز الـ QR"} 
                    value={formData.qr_code}
                    onChange={(e) => setFormData({...formData, qr_code: e.target.value})}
                    error={errors.qr_code?.[0]}
                    readOnly
                    className="bg-slate-50 cursor-not-allowed"
                />
                <Input 
                    label="اسم ولي الأمر" 
                    placeholder="أدخل اسم الأب أو الأم" 
                    value={formData.parent_name}
                    onChange={(e) => setFormData({...formData, parent_name: e.target.value})}
                    error={errors.parent_name?.[0]}
                />
                <Input 
                    label="رقم الهاتف" 
                    placeholder="06XXXXXXXX" 
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    error={errors.phone?.[0]}
                    icon={Phone}
                />
                <Select 
                    label="المؤسسة" 
                    options={[
                        { value: '', label: 'اختر المؤسسة' },
                        ...(institutions?.map(i => ({ value: String(i.id), label: i.name })) || [])
                    ]}
                    value={formData.institution_id}
                    onChange={(e) => setFormData({...formData, institution_id: e.target.value})}
                    error={errors.institution_id?.[0]}
                />
                <Select 
                    label="المستوى" 
                    options={[
                        { value: '', label: 'اختر المستوى' },
                        { value: 'الاولى اعدادي', label: 'الاولى اعدادي' },
                        { value: 'الثانية اعدادي', label: 'الثانية اعدادي' },
                        { value: 'الثالثة اعدادي', label: 'الثالثة اعدادي' },
                        { value: 'جدع مشترك', label: 'جدع مشترك' },
                        { value: 'الاولى باكلوريا', label: 'الاولى باكلوريا' },
                        { value: 'الثانية باكلوريا', label: 'الثانية باكلوريا' }
                    ]}
                    value={formData.level}
                    onChange={(e) => setFormData({...formData, level: e.target.value})}
                    error={errors.level?.[0]}
                />
                <Select 
                    label="القسم / الصنف" 
                    options={[
                        { value: '', label: 'اختر القسم' },
                        ...(schoolClasses?.map(sc => ({ value: String(sc.id), label: sc.name })) || [])
                    ]}
                    value={formData.school_class_id}
                    onChange={(e) => setFormData({...formData, school_class_id: e.target.value})}
                    error={errors.school_class_id?.[0]}
                />
                <Select 
                    label="المجموعة" 
                    options={[
                        { value: '', label: 'اختر المجموعة' },
                        ...(groups?.map(g => ({ value: String(g.id), label: g.name })) || [])
                    ]}
                    value={formData.group_id}
                    onChange={(e) => setFormData({...formData, group_id: e.target.value})}
                    error={errors.group_id?.[0]}
                />
                <Select 
                    label="المنطقة" 
                    options={[
                        { value: '', label: 'اختر المنطقة' },
                        ...(regions?.map(r => ({ value: String(r.id), label: r.region_name })) || [])
                    ]}
                    value={formData.region_id}
                    onChange={handleRegionChange}
                    error={errors.region_id?.[0]}
                />
                <Select 
                    label={
                        <div className="flex items-center justify-between w-full">
                            <span>المكان</span>
                            {placesLoading && (
                                <span className="text-[10px] text-slate-400 font-normal animate-pulse">جاري التحميل...</span>
                            )}
                        </div>
                    }
                    options={[
                        { value: '', label: 'اختر المكان (اختياري)' },
                        ...(places?.map(p => ({ value: String(p.id), label: p.place_name })) || []),
                        // Add current student's place as a fallback option if not in the loaded list
                        ...(student?.place && !places.some(p => String(p.id) === String(student.place_id || student.place.id))
                            ? [{ value: String(student.place_id || student.place.id), label: student.place.place_name }]
                            : [])
                    ]}
                    value={formData.place_id}
                    onChange={(e) => setFormData({...formData, place_id: e.target.value})}
                    error={errors.place_id?.[0]}
                    placeholder={!formData.region_id ? "اختر منطقة أولاً" : (placesLoading ? "جاري تحميل الأماكن..." : (places.length === 0 ? "لا توجد أماكن لهذه المنطقة" : "اختر المكان أو ابحث..."))}
                    disabled={placesLoading || !formData.region_id}
                />
                <Select 
                    label="السنة الدراسية" 
                    options={[
                        { value: '', label: 'اختر السنة' },
                        ...(academicYears?.map(ay => ({ value: String(ay.id), label: ay.name })) || [])
                    ]}
                    value={formData.academic_year_id}
                    onChange={(e) => setFormData({...formData, academic_year_id: e.target.value})}
                    error={errors.academic_year_id?.[0]}
                />
                <Select 
                    label="الجنس" 
                    options={[
                        { value: '', label: 'اختر الجنس' },
                        { value: 'Male', label: 'ذكر' },
                        { value: 'Female', label: 'أنثى' }
                    ]}
                    value={formData.gender}
                    onChange={(e) => setFormData({...formData, gender: e.target.value})}
                    error={errors.gender?.[0]}
                />
                <Select 
                    label="حالة الطالب" 
                    options={[
                        { value: 'حالة طبيعية', label: 'حالة طبيعية' },
                        { value: 'يتيم (ة)', label: 'يتيم (ة)' },
                        { value: 'ذوي الاحتياجات خاصة', label: 'ذوي الاحتياجات خاصة' },
                        { value: 'عائلة معوزة', label: 'عائلة معوزة' },
                        { value: 'مرض مزمن (اب)', label: 'مرض مزمن (اب)' },
                        { value: 'مرض مزمن (أم)', label: 'مرض مزمن (أم)' }
                    ]}
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    error={errors.status?.[0]}
                />
                <Input 
                    label="ثمن الاشتراك (درهم)" 
                    type="number"
                    placeholder="0.00" 
                    value={formData.subscription_price}
                    onChange={(e) => setFormData({...formData, subscription_price: e.target.value})}
                    error={errors.subscription_price?.[0]}
                />
                <Input 
                    label="واجب الانخراط السنوي (درهم)" 
                    type="number"
                    placeholder="0.00" 
                    value={formData.registration_fee}
                    onChange={(e) => setFormData({...formData, registration_fee: e.target.value})}
                    error={errors.registration_fee?.[0]}
                />
                {student && (
                    <Input 
                        label="تاريخ الخروج" 
                        type="date"
                        value={formData.exit_date}
                        onChange={(e) => setFormData({...formData, exit_date: e.target.value})}
                        error={errors.exit_date?.[0]}
                    />
                )}
            </div>
            
            {formData.exit_date && (
                <div className="w-full">
                    <label className="block text-sm font-medium text-slate-700 mb-1">سبب الخروج</label>
                    <textarea 
                        className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all min-h-[100px]"
                        placeholder="اكتب سبب خروج التلميذ من خدمة النقل..." 
                        value={formData.exit_reason}
                        onChange={(e) => setFormData({...formData, exit_reason: e.target.value})}
                    ></textarea>
                     {errors.exit_reason && <p className="text-red-500 text-xs mt-1">{errors.exit_reason[0]}</p>}
                </div>
            )}
            
            <div className="w-full">
                <Input 
                    label="العنوان" 
                    placeholder="أدخل عنوان السكن بالتفصيل" 
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    error={errors.address?.[0]}
                />
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                <Button type="submit" isLoading={isLoading} className="flex-1">
                    {student ? 'تحديث البيانات' : 'حفظ الطالب'}
                </Button>
                <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => onSuccess()} 
                    className="flex-1"
                    disabled={isLoading}
                >
                    إلغاء
                </Button>
            </div>
        </form>
    );
};

const PrintSelectionModal = ({ onClose }) => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedPool, setSelectedPool] = useState([]); // Store full objects to preserve across pages
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [meta, setMeta] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchStudents = async () => {
            setLoading(true);
            try {
                // Fetch only active students for printing
                const response = await api.get(`/students?page=${currentPage}&search=${searchTerm}`);
                const data = response.data?.data || response.data || [];
                setStudents(data);
                setMeta(response.data?.meta || null);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching students:", err);
                setLoading(false);
            }
        };
        fetchStudents();
    }, [currentPage, searchTerm]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const toggleSelection = (student) => {
        if (selectedIds.includes(student.id)) {
            setSelectedIds(prev => prev.filter(i => i !== student.id));
            setSelectedPool(prev => prev.filter(s => s.id !== student.id));
        } else {
            setSelectedIds(prev => [...prev, student.id]);
            setSelectedPool(prev => [...prev, student]);
        }
    };

    const [isSelectingAll, setIsSelectingAll] = useState(false);
    const [allSelectedFromDB, setAllSelectedFromDB] = useState(false);

    const toggleAll = () => {
        const currentPageIds = students.map(s => s.id);
        const allOnPageSelected = currentPageIds.every(id => selectedIds.includes(id));

        if (allOnPageSelected) {
            setSelectedIds(prev => prev.filter(id => !currentPageIds.includes(id)));
            setSelectedPool(prev => prev.filter(s => !currentPageIds.includes(s.id)));
            setAllSelectedFromDB(false);
        } else {
            const newIds = [...selectedIds];
            const newPool = [...selectedPool];
            students.forEach(s => {
                if (!newIds.includes(s.id)) {
                    newIds.push(s.id);
                    newPool.push(s);
                }
            });
            setSelectedIds(newIds);
            setSelectedPool(newPool);
        }
    };

    const { success, error: toastError } = useToast();

    const selectAllDatabase = async () => {
        setIsSelectingAll(true);
        try {
            // Fetch ALL students (all=true) with current search/filter
            const response = await api.get(`/students?all=true&search=${searchTerm}`);
            const allStudents = response.data?.data || response.data || [];
            const activeStudents = allStudents.filter(s => s.is_active == 1);
            
            setSelectedIds(activeStudents.map(s => s.id));
            setSelectedPool(activeStudents);
            setAllSelectedFromDB(true);
            success(`تم تحديد ${activeStudents.length} طالب بنجاح`);
        } catch (err) {
            console.error("Error selecting all students:", err);
            toastError("حدث خطأ أثناء تحديد الكل");
        } finally {
            setIsSelectingAll(false);
        }
    };

    const handlePrint = () => {
        if (selectedIds.length === 0) return;
        localStorage.setItem('print_students', JSON.stringify(selectedPool));
        navigate('/print/student-cards');
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
                <Input 
                    placeholder="ابحث بالاسم، رقم مسار، أو رمز QR..." 
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="flex-1"
                />
            </div>

            {loading ? (
                <div className="flex justify-center p-8"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div></div>
            ) : (
                <>
                    <div className="flex flex-col gap-2 mb-2 px-1">
                        <div className="flex justify-between items-center bg-slate-50 p-2 rounded-lg border border-slate-100">
                            <span className="text-sm text-slate-500 font-medium pb-1">تم تحديد {selectedIds.length} طالب</span>
                            <button 
                                type="button" 
                                onClick={async () => {
                                    if (allSelectedFromDB || (meta && selectedIds.length === meta.total) || selectedIds.length > 0) {
                                        setSelectedIds([]);
                                        setSelectedPool([]);
                                        setAllSelectedFromDB(false);
                                    } else {
                                        await selectAllDatabase();
                                    }
                                }}
                                disabled={isSelectingAll}
                                className="text-xs text-primary font-bold hover:underline bg-primary/10 px-3 py-1.5 rounded disabled:opacity-50"
                            >
                                {isSelectingAll ? 'جاري التحديد...' : (allSelectedFromDB || (meta && selectedIds.length === meta.total) || selectedIds.length > 0) ? 'إلغاء تحديد الكل' : 'تحديد الكل'}
                            </button>
                        </div>
                    </div>

                    <div className="max-h-[300px] overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100 mb-4">
                        {students.length === 0 ? (
                            <div className="p-4 text-center text-slate-500 text-sm">لا يوجد نتائج</div>
                        ) : (
                            students.map(student => (
                                <div key={student.id} className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors">
                                    <input 
                                        type="checkbox" 
                                        checked={selectedIds.includes(student.id)}
                                        onChange={() => toggleSelection(student)}
                                        className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                                    />
                                    <div>
                                        <p className="font-medium text-slate-800 text-sm">{student.full_name}</p>
                                        <p className="text-xs text-slate-500">{student.code_massar} • {student.institution?.name || '-'}</p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Modal Pagination */}
                    {meta && meta.last_page > 1 && (
                        <div className="flex items-center justify-between pb-4">
                            <span className="text-xs text-slate-500">صفحة {currentPage} من {meta.last_page}</span>
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>السابق</Button>
                                <Button size="sm" variant="outline" onClick={() => setCurrentPage(p => Math.min(meta.last_page, p + 1))} disabled={currentPage === meta.last_page}>التالي</Button>
                            </div>
                        </div>
                    )}

                    <div className="flex gap-2 pt-4 border-t border-slate-100">
                        <Button 
                            className="flex-1 bg-primary text-white" 
                            onClick={handlePrint}
                            disabled={selectedIds.length === 0}
                        >
                            <Printer className="w-4 h-4 ml-2" />
                            طباعة ({selectedIds.length}) بطاقات
                        </Button>
                        <Button variant="outline" onClick={onClose} className="flex-1">إلغاء</Button>
                    </div>
                </>
            )}
        </div>
    );
};

const StudentPrintModal = ({ onClose, regions, schoolClasses, groups, academicYears, institutions }) => {
    const { selectedYear: currentYear } = useAuth();
    const [formData, setFormData] = useState({
        status: '',
        region_id: '',
        school_class_id: '',
        group_id: '',
        academic_year_id: '',
        institution_id: '',
        is_active: ''
    });

    const handlePrint = () => {
        const params = new URLSearchParams();
        if (formData.status) params.append('status', formData.status);
        if (formData.region_id) params.append('region_id', formData.region_id);
        if (formData.school_class_id) params.append('school_class_id', formData.school_class_id);
        if (formData.group_id) params.append('group_id', formData.group_id);
        if (formData.academic_year_id) params.append('academic_year_id', formData.academic_year_id);
        if (formData.institution_id) params.append('institution_id', formData.institution_id);
        
        if (formData.is_active !== '') {
            params.append('is_active', formData.is_active);
        } else {
            // When selecting "Everyone", we need this to override the backend global 'active' scope
            params.append('include_inactive', 'true');
        }
        
        window.open(`/print/students-filtered?${params.toString()}`, '_blank');
        onClose();
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4">
                <Select 
                    label="حالة التلميذ" 
                    options={[
                        { value: '', label: 'كل الحالات' },
                        { value: 'حالة طبيعية', label: 'حالة طبيعية' },
                        { value: 'يتيم (ة)', label: 'يتيم (ة)' },
                        { value: 'ذوي الاحتياجات خاصة', label: 'ذوي الاحتياجات خاصة' },
                        { value: 'عائلة معوزة', label: 'عائلة معوزة' },
                        { value: 'مرض مزمن (اب)', label: 'مرض مزمن (اب)' },
                        { value: 'مرض مزمن (أم)', label: 'مرض مزمن (أم)' }
                    ]}
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                />

                <Select 
                    label="وضعية الطالب" 
                    options={[
                        { value: '', label: 'الجميع (نشطون ومغادرون)' },
                        { value: '1', label: 'نشطون فقط' },
                        { value: '0', label: 'مغادرون فقط' }
                    ]}
                    value={formData.is_active}
                    onChange={(e) => {
                        const val = e.target.value;
                        setFormData({
                            ...formData, 
                            is_active: val,
                            // Clear academic year if not '0' (departed) to make it general
                            academic_year_id: val === '0' ? (currentYear?.id || '') : ''
                        });
                    }}
                />
                
                <Select 
                    label="المنطقة" 
                    options={[
                        { value: '', label: 'كل المناطق' },
                        ...(regions?.map(r => ({ value: r.id, label: r.region_name })) || [])
                    ]}
                    value={formData.region_id}
                    onChange={(e) => setFormData({...formData, region_id: e.target.value})}
                />

                <Select 
                    label="المؤسسة" 
                    options={[
                        { value: '', label: 'كل المؤسسات' },
                        ...(institutions?.map(inst => ({ value: inst.id, label: inst.name })) || [])
                    ]}
                    value={formData.institution_id}
                    onChange={(e) => setFormData({...formData, institution_id: e.target.value})}
                />
                
                <Select 
                    label="القسم / المستوى" 
                    options={[
                        { value: '', label: 'كل الأقسام' },
                        ...(schoolClasses?.map(sc => ({ value: sc.id, label: sc.name })) || [])
                    ]}
                    value={formData.school_class_id}
                    onChange={(e) => setFormData({...formData, school_class_id: e.target.value})}
                />
                
                <Select 
                    label="المجموعة" 
                    options={[
                        { value: '', label: 'كل المجموعات' },
                        ...(groups?.map(g => ({ value: g.id, label: g.name })) || [])
                    ]}
                    value={formData.group_id}
                    onChange={(e) => setFormData({...formData, group_id: e.target.value})}
                />

                {formData.is_active === '0' && (
                    <Select 
                        label="السنة الدراسية" 
                        options={[
                            { value: '', label: 'اختر السنة' },
                            ...(academicYears?.map(ay => ({ value: ay.id, label: ay.name })) || [])
                        ]}
                        value={formData.academic_year_id}
                        onChange={(e) => setFormData({...formData, academic_year_id: e.target.value})}
                    />
                )}
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                <Button onClick={handlePrint} className="flex-1">
                    <Printer className="w-4 h-4 ml-2" />
                    تأكيد الطباعة
                </Button>
                <Button 
                    type="button" 
                    variant="outline" 
                    onClick={onClose} 
                    className="flex-1"
                >
                    إلغاء
                </Button>
            </div>
        </div>
    );
};

const TransferModal = ({ onClose, schoolClasses, onSuccess }) => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [meta, setMeta] = useState(null);

    const [formData, setFormData] = useState({
        school_class_id: '',
        level: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const { success, error: toastError } = useToast();

    useEffect(() => {
        const fetchStudents = async () => {
            setLoading(true);
            try {
                // Fetch active students for transfer
                const response = await api.get(`/students?page=${currentPage}&search=${searchTerm}&is_active=1`);
                const data = response.data?.data || response.data || [];
                setStudents(data);
                setMeta(response.data?.meta || null);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching students:", err);
                setLoading(false);
            }
        };
        fetchStudents();
    }, [currentPage, searchTerm]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const toggleSelection = (student) => {
        if (selectedIds.includes(student.id)) {
            setSelectedIds(prev => prev.filter(i => i !== student.id));
        } else {
            setSelectedIds(prev => [...prev, student.id]);
        }
    };
    
    const [isSelectingAll, setIsSelectingAll] = useState(false);
    const [allSelectedFromDB, setAllSelectedFromDB] = useState(false);

    const selectAllDatabase = async () => {
        setIsSelectingAll(true);
        try {
            const response = await api.get(`/students?all=true&search=${searchTerm}&is_active=1`);
            const allStudents = response.data?.data || response.data || [];
            
            setSelectedIds(allStudents.map(s => s.id));
            setAllSelectedFromDB(true);
            success(`تم تحديد ${allStudents.length} طالب بنجاح`);
        } catch (err) {
            console.error("Error selecting all students:", err);
            toastError("حدث خطأ أثناء تحديد الكل");
        } finally {
            setIsSelectingAll(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedIds.length === 0) {
            toastError('يرجى تحديد التلاميذ أولاً');
            return;
        }
        if (!formData.school_class_id && !formData.level) {
            toastError('يرجى اختيار القسم أو المستوى الجديد');
            return;
        }

        setIsLoading(true);
        try {
            await api.post('/students/bulk-transfer', {
                student_ids: selectedIds,
                ...formData
            });
            success('تم نقل التلاميذ بنجاح');
            onSuccess();
            onClose();
        } catch (err) {
            toastError('حدث خطأ أثناء نقل التلاميذ');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
                <Input 
                    placeholder="ابحث بالاسم، رقم مسار، أو رمز QR..." 
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="flex-1"
                />
            </div>

            {loading ? (
                <div className="flex justify-center p-8"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div></div>
            ) : (
                <>
                    <div className="flex flex-col gap-2 mb-2 px-1">
                        <div className="flex justify-between items-center bg-slate-50 p-2 rounded-lg border border-slate-100">
                            <span className="text-sm text-slate-500 font-medium pb-1">تم تحديد {selectedIds.length} طالب لنقلهم</span>
                            <button 
                                type="button" 
                                onClick={async () => {
                                    if (allSelectedFromDB || (meta && selectedIds.length === meta.total) || selectedIds.length > 0) {
                                        setSelectedIds([]);
                                        setAllSelectedFromDB(false);
                                    } else {
                                        await selectAllDatabase();
                                    }
                                }}
                                disabled={isSelectingAll}
                                className="text-xs text-primary font-bold hover:underline bg-primary/10 px-3 py-1.5 rounded disabled:opacity-50"
                            >
                                {isSelectingAll ? 'جاري التحديد...' : (allSelectedFromDB || (meta && selectedIds.length === meta.total) || selectedIds.length > 0) ? 'إلغاء تحديد الكل' : 'تحديد الكل'}
                            </button>
                        </div>
                    </div>

                    <div className="max-h-[250px] overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100 mb-4">
                        {students.length === 0 ? (
                            <div className="p-4 text-center text-slate-500 text-sm">لا يوجد نتائج</div>
                        ) : (
                            students.map(student => (
                                <div key={student.id} className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors">
                                    <input 
                                        type="checkbox" 
                                        checked={selectedIds.includes(student.id)}
                                        onChange={() => toggleSelection(student)}
                                        className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                                    />
                                    <div>
                                        <p className="font-medium text-slate-800 text-sm">{student.full_name}</p>
                                        <p className="text-xs text-slate-500">{student.code_massar} • {student.school_class?.name || '-'}</p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {meta && meta.last_page > 1 && (
                        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                            <span className="text-xs text-slate-500">صفحة {currentPage} من {meta.last_page}</span>
                            <div className="flex gap-2">
                                <Button type="button" size="sm" variant="outline" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>السابق</Button>
                                <Button type="button" size="sm" variant="outline" onClick={() => setCurrentPage(p => Math.min(meta.last_page, p + 1))} disabled={currentPage === meta.last_page}>التالي</Button>
                            </div>
                        </div>
                    )}
                </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <Select 
                    label="المستوى الجديد" 
                    options={[
                        { value: '', label: 'بدون تغيير' },
                        { value: 'الاولى اعدادي', label: 'الاولى اعدادي' },
                        { value: 'الثانية اعدادي', label: 'الثانية اعدادي' },
                        { value: 'الثالثة اعدادي', label: 'الثالثة اعدادي' },
                        { value: 'جدع مشترك', label: 'جدع مشترك' },
                        { value: 'الاولى باكلوريا', label: 'الاولى باكلوريا' },
                        { value: 'الثانية باكلوريا', label: 'الثانية باكلوريا' }
                    ]}
                    value={formData.level}
                    onChange={(e) => setFormData({...formData, level: e.target.value})}
                />

                <Select 
                    label="القسم الجديد" 
                    options={[
                        { value: '', label: 'بدون تغيير' },
                        ...(schoolClasses?.map(sc => ({ value: sc.id, label: sc.name })) || [])
                    ]}
                    value={formData.school_class_id}
                    onChange={(e) => setFormData({...formData, school_class_id: e.target.value})}
                />
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                <Button type="submit" isLoading={isLoading} className="flex-1 bg-blue-600 text-white">
                    تأكيد النقل
                </Button>
                <Button 
                    type="button" 
                    variant="outline" 
                    onClick={onClose} 
                    className="flex-1"
                    disabled={isLoading}
                >
                    إلغاء
                </Button>
            </div>
        </form>
    );
};

export default StudentList;
